import { useState, useEffect } from 'react';
import moment from 'moment';

export const useCalculatorState = () => {
  // Original calculator states
  const [balance, setBalance] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [additionalSpending, setAdditionalSpending] = useState('');
  const [calculationType, setCalculationType] = useState('debt');
  const [years, setYears] = useState('');
  const [months, setMonths] = useState('');
  const [targetMonths, setTargetMonths] = useState('');
  const [targetEndBalance, setTargetEndBalance] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [contributionMonths, setContributionMonths] = useState('');
  const [annualFeeRate, setAnnualFeeRate] = useState('');
  const [startDate, setStartDate] = useState(moment());

  // New mortgage-specific states
  const [mortgageAmount, setMortgageAmount] = useState('');
  const [propertyValue, setPropertyValue] = useState('');
  const [deposit, setDeposit] = useState('');
  const [fixedRate, setFixedRate] = useState('');
  const [fixedTerm, setFixedTerm] = useState('');
  const [totalTerm, setTotalTerm] = useState('');
  const [variableRate, setVariableRate] = useState('');
  const [mortgageType, setMortgageType] = useState('repayment');
  const [mortgageStartMonth, setMortgageStartMonth] = useState(moment().month());
  const [mortgageStartYear, setMortgageStartYear] = useState(moment().year());
  const [monthlyOverpayment, setMonthlyOverpayment] = useState(0);
  const [lumpSumOverpayments, setLumpSumOverpayments] = useState([]);
  const [overpaymentStrategy, setOverpaymentStrategy] = useState('reduce-term'); // or 'reduce-payment'

  useEffect(() => {
    switch (calculationType) {
      case 'debt':
        setInterestRate((prevRate) => prevRate === '' ? '18.9' : prevRate);
        break;
      case 'investment':
        setInterestRate((prevRate) => prevRate === '' ? '7.0' : prevRate);
        break;
      case 'mortgage':
        setFixedRate((prevRate) => prevRate === '' ? '4.5' : prevRate);
        setVariableRate((prevRate) => prevRate === '' ? '5.5' : prevRate);
        break;
      default:
        break;
    }
  }, [calculationType]);

  const resetState = () => {
    // Reset original calculator states
    setBalance('');
    setInterestRate('');
    setMonthlyContribution('');
    setAdditionalSpending('');
    setCalculationType('debt');
    setYears('');
    setMonths('');
    setTargetMonths('');
    setTargetEndBalance('');
    setCurrency('GBP');
    setAnnualFeeRate('');
    setStartDate(moment());

    // Reset mortgage-specific states
    setMortgageAmount('');
    setPropertyValue('');
    setDeposit('');
    setFixedRate('');
    setFixedTerm('');
    setTotalTerm('');
    setVariableRate('');
    setMortgageType('repayment');
    setMortgageStartMonth(moment().month());
    setMortgageStartYear(moment().year());
    setMonthlyOverpayment(0);
    setLumpSumOverpayments([]);
    setOverpaymentStrategy('reduce-term');
  };

  const resetTypeSpecificFields = (newType) => {
    setBalance('');
    
    switch (newType) {
      case 'debt':
        setInterestRate('18.9');
        setMonthlyContribution('');
        setAdditionalSpending('');
        setTargetMonths('');
        setYears('');
        setMonths('');
        setContributionMonths('');
        setTargetEndBalance('');
        setAnnualFeeRate('');
        // Clear mortgage fields
        setMortgageAmount('');
        setPropertyValue('');
        setDeposit('');
        setFixedRate('');
        setFixedTerm('');
        setTotalTerm('');
        setVariableRate('');
        break;

      case 'investment':
        setInterestRate('7.0');
        setMonthlyContribution('');
        setAdditionalSpending('');
        setTargetMonths('');
        setYears('');
        setMonths('');
        setContributionMonths('');
        setTargetEndBalance('');
        setAnnualFeeRate('');
        // Clear mortgage fields
        setMortgageAmount('');
        setPropertyValue('');
        setDeposit('');
        setFixedRate('');
        setFixedTerm('');
        setTotalTerm('');
        setVariableRate('');
        break;

      case 'mortgage':
        // Clear debt/investment fields
        setInterestRate('');
        setMonthlyContribution('');
        setAdditionalSpending('');
        setTargetMonths('');
        setContributionMonths('');
        setTargetEndBalance('');
        setAnnualFeeRate('');
        // Set default mortgage values
        setFixedRate('4.5');
        setFixedTerm('5');
        setTotalTerm('25');
        setVariableRate('5.5');
        setMortgageType('repayment');
        setMortgageStartMonth(moment().month());
        setMortgageStartYear(moment().year());
        setMonthlyOverpayment(0);
        setLumpSumOverpayments([]);
        setOverpaymentStrategy('reduce-term');
        break;

      default:
        break;
    }
  };

  const setCalculationTypeAndReset = (newType) => {
    setCalculationType(newType);
    resetTypeSpecificFields(newType);
  };

  const validateMortgageInputs = () => {
    if (!propertyValue || !fixedRate || !fixedTerm || !totalTerm || !variableRate) {
      return 'All mortgage fields are required';
    }
    if (deposit && parseFloat(deposit) >= parseFloat(propertyValue)) {
      return 'Deposit cannot be greater than or equal to property value';
    }
    if (parseFloat(fixedTerm) > parseFloat(totalTerm)) {
      return 'Fixed term cannot be longer than total term';
    }
    return '';
  };

  return {
    // Original calculator states
    balance,
    interestRate,
    monthlyContribution,
    additionalSpending,
    calculationType,
    years,
    months,
    targetMonths,
    targetEndBalance,
    currency,
    contributionMonths,
    annualFeeRate,
    startDate,

    // Mortgage-specific states
    mortgageAmount,
    propertyValue,
    deposit,
    fixedRate,
    fixedTerm,
    totalTerm,
    variableRate,
    mortgageType,
    mortgageStartMonth,
    mortgageStartYear,

    // Original setters
    setBalance,
    setInterestRate,
    setMonthlyContribution,
    setAdditionalSpending,
    setCalculationType,
    setYears,
    setMonths,
    setTargetMonths,
    setTargetEndBalance,
    setCurrency,
    setContributionMonths,
    setAnnualFeeRate,
    setStartDate,

    // Mortgage-specific setters
    setMortgageAmount,
    setPropertyValue,
    setDeposit,
    setFixedRate,
    setFixedTerm,
    setTotalTerm,
    setVariableRate,
    setMortgageType,
    setMortgageStartMonth,
    setMortgageStartYear,
    monthlyOverpayment,
    lumpSumOverpayments,
    overpaymentStrategy,
    setMonthlyOverpayment,
    setLumpSumOverpayments,
    setOverpaymentStrategy,

    // Helper functions
    resetState,
    setCalculationTypeAndReset,
    resetTypeSpecificFields,
    validateMortgageInputs,
  };
};